import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SEOHead from "../../components/shared/SEOHead.jsx";
import JsonLd from "../../components/shared/JsonLd.jsx";
import { buildBreadcrumbJsonLd, buildFaqJsonLd } from "../../utils/jsonld.js";
import SimplePage from "../../components/shared/SimplePage.jsx";
import { SUPPORT_PATH, SUPPORT_SUBPAGE_PATH } from "./paths.js";
import { ABOUT_PATH, SUBPAGE_PATH } from "../About/paths.js";
import { SponsoringDe, SponsoringEn } from "./SponsoringContent.jsx";
import { CONTACT_SUBJECT_HASH } from "../Kontakt.jsx";
import { MembershipDe, MembershipEn, MEMBERSHIP_FAQ } from "./MembershipContent.jsx";
import { VolunteerDe, VolunteerEn, VOLUNTEER_FAQ } from "./VolunteerContent.jsx";

const KONTAKT_PATH = { de: "kontakt", en: "contact" };
const SPONSORING_CONTENT = { de: SponsoringDe, en: SponsoringEn };
const MEMBERSHIP_CONTENT = { de: MembershipDe, en: MembershipEn };
const MEMBERSHIP_CTA = { de: "Jetzt Förderer werden", en: "Become a Supporter Now" };
const VOLUNTEER_CONTENT = { de: VolunteerDe, en: VolunteerEn };

/**
 * Generic value-proposition copy is fine to write here (no fabricated
 * org-specific facts) — but concrete numbers (membership fee, sponsoring
 * tiers) are left as placeholders, same treatment as Impressum's bank
 * details, since those are the association's decision, not code's.
 */
export default function SupportSubpage({ lang, topic }) {
  const { t } = useTranslation();
  const otherLang = lang === "de" ? "en" : "de";
  const path = `/${SUPPORT_PATH[lang]}/${SUPPORT_SUBPAGE_PATH[topic][lang]}`;
  const altPath = `/${SUPPORT_PATH[otherLang]}/${SUPPORT_SUBPAGE_PATH[topic][otherLang]}`;

  if (topic === "sponsoring") {
    const SponsoringBody = SPONSORING_CONTENT[lang];
    const h1 =
      lang === "de"
        ? "Werde Teil der Legende: Sponsoring für LARP & Jugendarbeit"
        : "Become Part of the Legend: Sponsorship for LARP & Youth Work";
    const breadcrumbItems = [
      { name: t("nav.home"), path: "/" },
      { name: t(`support.${topic}.title`), path },
    ];
    return (
      <>
        <SEOHead lang={lang} path={path} altPath={altPath} title={t("support.sponsoring.metaTitle")} description={t("support.sponsoring.metaDescription")} />
        <JsonLd data={buildBreadcrumbJsonLd(breadcrumbItems, lang)} />
        <SimplePage title={h1} maxWidth="900px">
          <SponsoringBody />
          <Link to={`/${lang}/${KONTAKT_PATH[lang]}${CONTACT_SUBJECT_HASH.sponsoring}`} className="tl-pill tl-pill-dark" style={{ alignSelf: "flex-start", padding: "12px 24px" }}>
            {t("support.contactCta")}
          </Link>
        </SimplePage>
      </>
    );
  }

  if (topic === "membership") {
    const MembershipBody = MEMBERSHIP_CONTENT[lang];
    const breadcrumbItems = [
      { name: t("nav.home"), path: "/" },
      { name: t(`support.${topic}.title`), path },
    ];
    return (
      <>
        <SEOHead lang={lang} path={path} altPath={altPath} title={t("support.membership.metaTitle")} description={t("support.membership.metaDescription")} />
        <JsonLd data={buildBreadcrumbJsonLd(breadcrumbItems, lang)} />
        <JsonLd data={buildFaqJsonLd(MEMBERSHIP_FAQ[lang].map(({ question, answer }) => ({ question, answer })))} />
        <SimplePage title={t("support.membership.h1")} maxWidth="900px">
          <MembershipBody />
          <Link to={`/${lang}/${KONTAKT_PATH[lang]}`} className="tl-pill tl-pill-dark" style={{ alignSelf: "flex-start", padding: "12px 24px" }}>
            {MEMBERSHIP_CTA[lang]}
          </Link>
        </SimplePage>
      </>
    );
  }

  if (topic === "volunteer") {
    const VolunteerBody = VOLUNTEER_CONTENT[lang];
    const jugendschutzPath = `/${lang}/${ABOUT_PATH[lang]}/${SUBPAGE_PATH.jugendschutz[lang]}`;
    const verhaltenskodexPath = `/${lang}/${ABOUT_PATH[lang]}/${SUBPAGE_PATH.conduct[lang]}`;
    const breadcrumbItems = [
      { name: t("nav.home"), path: "/" },
      { name: t(`support.${topic}.title`), path },
    ];
    return (
      <>
        <SEOHead lang={lang} path={path} altPath={altPath} title={t("support.volunteer.metaTitle")} description={t("support.volunteer.metaDescription")} />
        <JsonLd data={buildBreadcrumbJsonLd(breadcrumbItems, lang)} />
        <JsonLd data={buildFaqJsonLd(VOLUNTEER_FAQ[lang].map(({ question, answer }) => ({ question, answer })))} />
        <SimplePage title={t("support.volunteer.h1")} maxWidth="920px">
          <VolunteerBody jugendschutzPath={jugendschutzPath} verhaltenskodexPath={verhaltenskodexPath} />
          <Link to={`/${lang}/${KONTAKT_PATH[lang]}`} className="tl-pill tl-pill-dark" style={{ alignSelf: "flex-start", padding: "12px 24px" }}>
            {t("support.contactCta")}
          </Link>
        </SimplePage>
      </>
    );
  }

  return (
    <>
      <SEOHead lang={lang} path={path} altPath={altPath} title={t(`support.${topic}.title`)} />
      <SimplePage title={t(`support.${topic}.title`)}>
        <p style={{ fontSize: "16px", lineHeight: 1.7, color: "rgba(10,9,7,0.72)", margin: 0 }}>{t(`support.${topic}.body`)}</p>
        <p className="placeholder" style={{ fontSize: "14px", color: "rgba(10,9,7,0.7)", margin: 0 }}>
          {t(`support.${topic}.placeholder`)}
        </p>
        <Link to={`/${lang}/${KONTAKT_PATH[lang]}`} className="tl-pill tl-pill-dark" style={{ alignSelf: "flex-start", padding: "12px 24px" }}>
          {t("support.contactCta")}
        </Link>
      </SimplePage>
    </>
  );
}
